import React from "react";
import { Divider } from "@mui/material";
import PropTypes from "prop-types";

/**
 * CDivider - A wrapper for MUI Divider with additional functionality
 * 
 * @param {Object} props
 * @param {string} props.orientation - The component orientation (horizontal or vertical)
 * @param {string} props.variant - The divider variant (fullWidth, inset, middle)
 * @param {number} props.light - If true, the divider will have a lighter color
 * @param {string} props.textAlign - The text alignment when the variant is middle (left, right, center)
 * @param {React.ReactNode} props.children - The content of the divider (when variant is middle)
 * @param {Object} props.sx - Additional styling for the divider
 * @param {Object} props.theme - Theme object for styling
 * @returns {JSX.Element}
 */
const CDivider = ({
  orientation = "horizontal",
  variant = "fullWidth",
  light = false,
  textAlign = "center",
  children,
  sx = {},
  theme,
  ...rest
}) => {
  // Default styles based on theme (if provided)
  const defaultSx = theme
    ? {
        borderColor: theme.palette.divider,
        my: theme.spacing(2),
      }
    : {};

  return (
    <Divider
      orientation={orientation}
      variant={variant}
      light={light}
      textAlign={textAlign}
      sx={{ ...defaultSx, ...sx }}
      {...rest}
    >
      {children}
    </Divider>
  );
};

CDivider.propTypes = {
  orientation: PropTypes.oneOf(['horizontal', 'vertical']),
  variant: PropTypes.oneOf(['fullWidth', 'inset', 'middle']),
  light: PropTypes.bool,
  textAlign: PropTypes.oneOf(['center', 'left', 'right']),
  children: PropTypes.node,
  sx: PropTypes.object,
  theme: PropTypes.object,
};

export default CDivider;